import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'

import CreateNote from './Create/CreateNote'
import SelectAccommodationType from './Variety/SelectAccommodationType'
import SelectFlightType from './Variety/SelectFlightType'
import SelectThingsToDoType from './Variety/SelectThingsToDoType'
import SelectTransportType from './Variety/SelectTransportType'
import SelectBookingToAdd from '../Bookings/SelectBookingToAdd'

import { fetchOrders } from 'actions/OrderActions'
import { addToTripClickEvent } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { haveOrdersBeenFetched } from 'lib/order/orderUtils'
import { isStandaloneFlightsEnabled } from 'selectors/flightsSelectors'
import { getUpcomingOrders } from 'selectors/orderSelectors'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { ParentItemMetadata } from 'tripPlanner/models'
import { getFutureFilteredOrdersNotInTrip } from 'tripPlanner/utils/summary'
import config from 'constants/config'
import NLPItemCreate from 'tripPlanner/components/NLPItemCreate/NLPItemCreate'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import InputWrap from 'components/Common/Form/Input/InputWrap'
import Hr from 'components/Common/Hr'
import NestableClickable from 'components/Common/NestableClickable'
import IconButton from 'LuxKit/Button/IconButton'
import LineCopyIcon from 'LuxKit/Icons/line/LineCopyIcon'
import Caption from 'LuxKit/Typography/Caption'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { copyValueToClipboard } from 'lib/web/clipboardUtils'
import { rem } from 'polished'
import styled from 'styled-components'
import ToggleButton from 'LuxKit/ToggleButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'
import type { ParentItemType } from 'tripPlanner/types/tripItem'
import type { CreateTripItemRankSpec } from 'tripPlanner/api/tripItem/types'

const EmailText = styled(BodyText)`
  width: 100%;
  padding: ${rem(12)};
  color: ${(props) => props.theme.palette.neutral.default.one};
  padding-right: ${rem(48)};
  cursor: text;
`

const ItemTypesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const selectableItems: Array<Exclude<ParentItemType, 'TOUR_AND_CRUISES' | 'TOURS' | 'CRUISES' | 'CAR'>> = [
  'LE_BOOKINGS',
  'THINGS_TO_DO',
  'ACCOMMODATION',
  'FLIGHT',
  'TRANSPORT',
  'NOTE',
]

interface MappedStateProps {
  memberId?: string
  standaloneFlightsEnabled: boolean
}

interface Props {
  tripId: string
  defaultDate?: string
  rankSpec?: CreateTripItemRankSpec
}

function SelectItemToAdd({
  tripId,
  defaultDate,
  standaloneFlightsEnabled,
  rankSpec,
}: Props & MappedStateProps) {
  const intl = useIntl()
  const { data: trip, isLoading: isTripLoading } = useTrip({ tripId })
  const openModal = useOpenModal()
  const { closeModal, goBack } = useModalCallbacks()

  const dispatch = useAppDispatch()

  const orders = useAppSelector((state) => getUpcomingOrders(state))
  const ordersFetched = useAppSelector((state) =>
    haveOrdersBeenFetched(state, 'upcoming'),
  )

  useEffect(() => {
    if (!ordersFetched) {
      dispatch(fetchOrders({ status: 'upcoming' }))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filteredOrders = useMemo(
    () => getFutureFilteredOrdersNotInTrip(orders, trip),
    [orders, trip],
  )

  const addItem = (item: (typeof selectableItems)[number]) => {
    switch (item) {
      case 'LE_BOOKINGS':
        fireInteractionEvent(addToTripClickEvent('modal_le_booking'))
        openModal(SelectBookingToAdd, { tripId })
        break
      case 'ACCOMMODATION':
        fireInteractionEvent(addToTripClickEvent('modal_accommodation'))
        openModal(SelectAccommodationType, { tripId, defaultDate, rankSpec })
        break
      case 'FLIGHT':
        fireInteractionEvent(addToTripClickEvent('modal_flights'))
        if (standaloneFlightsEnabled) {
          openModal(SelectFlightType, { tripId, defaultDate, rankSpec })
        } else {
          openModal(
            SelectFlightType,
            { tripId, customOnly: true, rankSpec },
            { size: 'S' },
          )
        }
        break
      case 'THINGS_TO_DO':
        fireInteractionEvent(addToTripClickEvent('modal_experiences'))
        openModal(SelectThingsToDoType, { tripId, defaultDate, rankSpec })
        break
      case 'TRANSPORT':
        fireInteractionEvent(addToTripClickEvent('modal_transport'))
        openModal(SelectTransportType, { tripId, defaultDate, rankSpec })
        break
      case 'NOTE':
        fireInteractionEvent(addToTripClickEvent('modal_notes'))
        openModal(CreateNote, { tripId, defaultDate, rankSpec })
        break
      default:
        return assertUnreachable(item)
    }
  }

  const textRef = useRef<HTMLDivElement>(null)

  const emailAddress = `${tripId}@${config.TRIP_DOCUMENT_FORWARDING_EMAIL_DOMAIN}`

  const copyEmailToClipboard = useCallback(() => {
    copyValueToClipboard(emailAddress)
    showSnackbar(intl.formatMessage({
      id: 'ni1iEGjb6VEEA6TICvTS',
      defaultMessage: 'Email copied to clipboard',
      description: 'Snackbar message after copying trip email address to clipboard',
    }), 'success')
  }, [emailAddress, intl])

  const onTextClick = useCallback(() => {
    const textDiv = textRef.current
    if (window.getSelection && textDiv) {
      const range = document.createRange()
      range.selectNode(textDiv)
      window.getSelection()!.removeAllRanges()
      window.getSelection()!.addRange(range)
    }
    copyEmailToClipboard()
  }, [copyEmailToClipboard])

  const addToTripTitle = intl.formatMessage({
    id: 'X-iQrSTTCKbzfK9CcTv1',
    defaultMessage: 'Add to trip',
    description: 'Modal title for adding items to a trip in the trip planner feature',
  })

  const searchActivitiesHeading = intl.formatMessage({
    id: 'fW7--LkFrF1YR30xgARK',
    defaultMessage: 'Search for activities to add',
    description: 'Heading for the NLP activity search section in add to trip modal',
  })

  const addIndividualItemHeading = intl.formatMessage({
    id: '0DLZrrzUKna8E-xAU8fq',
    defaultMessage: 'Add an individual item',
    description: 'Heading for the individual item type selection section in add to trip modal',
  })

  const forwardEmailHeading = intl.formatMessage({
    id: 'evZS7Th-nWGovHDpL_wj',
    defaultMessage: 'Forward email',
    description: 'Heading for email forwarding section in add to trip modal',
  })

  const forwardEmailCaption = intl.formatMessage({
    id: 'x6hdPnRJvxj6texAehvR',
    defaultMessage: 'Forward any booking confirmations or documents and we’ll add them to your trip.',
    description: 'Caption explaining how email forwarding works for trip items',
  })

  return (
    <>
      <ModalHeader
        title={addToTripTitle}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={24}>
            {config.ENABLE_TRIP_ITEM_NLP && trip && <>
              <VerticalSpacer gap={16}>
                <BodyText variant="large" weight="bold">
                  {searchActivitiesHeading}
                </BodyText>
                <NLPItemCreate trip={trip} onItemCreated={closeModal} date={defaultDate} dropdownAutoHeight rankSpec={rankSpec} />
              </VerticalSpacer>

              <div>
                <Hr />
              </div>
            </>}

            <VerticalSpacer gap={16}>
              <BodyText variant="large" weight="bold">
                {addIndividualItemHeading}
              </BodyText>
              <ItemTypesGrid>
                {selectableItems.map((item) => {
                  const { icon: Icon, name } = ParentItemMetadata[item]

                  if (item === 'LE_BOOKINGS') {
                    if (filteredOrders.length < 1) {
                      return null
                    }
                    return (
                      <ToggleButton
                        key={item}
                        kind="secondary"
                        startIcon={<Icon size="S" colour="neutral-one" />}
                        title={!isTripLoading ? intl.formatMessage({ id: '7EIXWWtuBHemCtI6nlrm', defaultMessage: '{name} ({count})', description: 'Toggle button title for LE bookings with count of available bookings' }, { name: intl.formatMessage(name), count: filteredOrders.length }) : intl.formatMessage(name)}
                        onClick={() => addItem(item)}
                      />
                    )
                  }

                  return (
                    <ToggleButton
                      key={item}
                      kind="secondary"
                      startIcon={<Icon size="S" />}
                      title={intl.formatMessage(name)}
                      onClick={() => addItem(item)}
                    />
                  )
                })}
              </ItemTypesGrid>
            </VerticalSpacer>

            {config.ENABLE_TRIP_DOCUMENT_EMAIL_FORWARDING &&
              <>
                <div>
                  <Hr />
                </div>
                <VerticalSpacer gap={16}>
                  <div>
                    <BodyText variant="large" weight="bold">
                      {forwardEmailHeading}
                    </BodyText>
                    <Caption variant="large">
                      {forwardEmailCaption}
                    </Caption>
                  </div>

                  <NestableClickable onClick={onTextClick}>
                    <InputWrap endIcon={<IconButton onClick={copyEmailToClipboard} kind="tertiary"><LineCopyIcon /></IconButton>} noValidationSpacing>
                      <div ref={textRef}>
                        <EmailText variant="medium" wrap="word-wrap">{emailAddress}</EmailText>
                      </div>
                    </InputWrap>
                  </NestableClickable>
                </VerticalSpacer>
              </>}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default connect<MappedStateProps, unknown, Props, App.State>(
  (state) => ({
    memberId: state.auth.account.memberId,
    standaloneFlightsEnabled: isStandaloneFlightsEnabled(state),
  }),
)(SelectItemToAdd)
